import '~/lib/config/yup';

import { lazy, Suspense } from 'react';
import { render } from 'react-dom';
import { BrowserRouter } from 'react-router-dom';
import * as smoothscroll from 'smoothscroll-polyfill';
import { SWRConfig } from 'swr';

import { ThemeProvider } from '@gmm/ui';
import { Auth, AuthProvider } from '~/lib/auth';
import { Config } from '~/lib/config/config';
import ErrorBoundary from '~/lib/errorBoundary';
import {
  LaunchDarklyProvider,
  LDReadyGate,
} from '~/lib/launchDarkly/provider';
import { getUserConfirmation } from '~/lib/routing';

import { getConfig } from './environmentConfig';
import { mutateDraft, sdkFetcher } from './lib/swr';

async function main(): Promise<void> {
  smoothscroll.polyfill();

  // Initializes locales
  await import('~/lib/config/i18n');

  // const isProduction = process.env.NODE_ENV === 'production';
  // Hot reloading. Uncomment below when HMR is fixed.
  // if (!isProduction && module.hot) {
  // module.hot.accept();
  // }

  const App = lazy(() => import('./app'));
  const AppProvider = lazy(() => import('./appProvider'));
  const swrValue = {
    fetcher: sdkFetcher,
    revalidateOnFocus: false,
    use: [mutateDraft],
  };

  const config = await getConfig();

  render(
    <BrowserRouter getUserConfirmation={getUserConfirmation}>
      <SWRConfig value={swrValue}>
        <AuthProvider>
          <ThemeProvider>
            <LaunchDarklyProvider clientSideID={config.LD_CLIENT_SIDE_ID}>
              <Config />
              <ErrorBoundary>
                <Auth>
                  <LDReadyGate>
                    <Suspense fallback={<noscript />}>
                      <AppProvider>
                        <App />
                      </AppProvider>
                    </Suspense>
                  </LDReadyGate>
                </Auth>
              </ErrorBoundary>
            </LaunchDarklyProvider>
          </ThemeProvider>
        </AuthProvider>
      </SWRConfig>
    </BrowserRouter>,
    document.getElementById('app') as HTMLElement,
  );
}

main();
